import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, Button, Grid } from "@mui/material";
import apiService from "../../../../service/Api";
import StreamLocationsCard, {
  type StreamLocationDatum,
} from "./StreamLocationsCard";
import ViewsCard from "./ViewsCard";
import TopStreamsTable, { type TopStreamItem } from "./TopStreamsTable";
import CountryViewsMapCard, { type CountryViewsDatum } from "./CountreyViews";
import PlatformBreakdownCard from "./PlatformBreakdownCard";
import { countryName } from "./utils/country";

interface LocationReportApiItem {
  country: string;
  views: number;
  percent: string;
}

interface LocationReportResponse {
  data: LocationReportApiItem[];
  total_views: number;
}

interface TopPerformerItem {
  id: number;
  name: string;
  location: string;
  city: string;
  views: number;
}

interface TopPerformersResponse {
  success: boolean;
  message: string;
  data: TopPerformerItem[];
}

interface PlatformReportApiItem {
  Platform: string;
  Percent: string;
  Views: number;
  color: string;
}

interface PlatformReportResponse {
  data: PlatformReportApiItem[];
  total_views?: number;
}

const LOCATION_COLOR_PALETTE = [
  "#5AB251",
  "#F2B90D",
  "#7C4DFF",
  "#00ACC1",
  "#EC407A",
  "#8D6E63",
];

const PLATFORM_COLORS: Record<string, string> = {
  Facebook: "#1877F2",
  Muzukuru: "#059669",
  Youtube: "#FF0000",
};

const StreamsDashboard = () => {
  const navigate = useNavigate();
  const [syncingReports, setSyncingReports] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const [streamLocationsData, setStreamLocationsData] = useState<
    StreamLocationDatum[]
  >([]);
  const [countryViewsData, setCountryViewsData] = useState<CountryViewsDatum[]>(
    [],
  );
  const [totalViews, setTotalViews] = useState(0);
  const [viewsData, setViewsData] = useState<{
    platforms: string[];
    values: number[];
    colors: string[];
  }>({
    platforms: [],
    values: [],
    colors: [],
  });
  const [topStreamsData, setTopStreamsData] = useState<TopStreamItem[]>([]);

  const refreshReports = async () => {
    try {
      const [topRes, platformRes, locationRes] = await Promise.all([
        apiService.get<TopPerformersResponse>(
          "api/funeral-home/dashboard/top-performing-streams/",
        ),
        apiService.get<PlatformReportResponse>(
          "api/funeral-home/dashboard/platform-report/",
        ),
        apiService.get<LocationReportResponse>(
          "api/funeral-home/dashboard/location-report/",
        ),
      ]);

      const topApiData = Array.isArray(topRes.data.data)
        ? topRes.data.data
        : [];
      const mappedTop: TopStreamItem[] = topApiData.map((item) => ({
        id: item.id,
        name: item.name,
        location: item.location,
        muzukuru: item.views,
        youtube: item.views,
        facebook: 0,
        church: 0,
      }));
      setTopStreamsData(mappedTop);

      const platformApiData = Array.isArray(platformRes.data.data)
        ? platformRes.data.data
        : [];
      setViewsData({
        platforms: platformApiData.map((item) => item.Platform),
        values: platformApiData.map((item) => item.Views),
        colors: platformApiData.map(
          (item) => item.color || PLATFORM_COLORS[item.Platform] || "#E0E0E0",
        ),
      });

      const locationApiData = Array.isArray(locationRes.data.data)
        ? locationRes.data.data
        : [];
      const mappedLocations: StreamLocationDatum[] = locationApiData.map(
        (item, index) => ({
          id: index,
          label: countryName(item.country),
          value: parseFloat(item.percent) || 0,
          color: LOCATION_COLOR_PALETTE[index % LOCATION_COLOR_PALETTE.length],
        }),
      );
      setStreamLocationsData(mappedLocations);
      setTotalViews(locationRes.data.total_views);
      setCountryViewsData(
        locationApiData.map((item) => ({
          code: item.country,
          views: item.views,
        })),
      );
    } catch (err) {
      console.error("Error refreshing stream dashboard reports:", err);
    }
  };

  useEffect(() => {
    void refreshReports();
  }, []);

  const handleSyncReports = async () => {
    try {
      setSyncingReports(true);
      setSyncMessage(null);
      const response = await apiService.post<{
        success: boolean;
        message: string;
      }>("api/funeral-home/reports/sync-stream-reports/");
      setSyncMessage(response.data.message || "Stream reports sync started.");
      await refreshReports();
    } catch (err) {
      console.error("Error starting stream report sync:", err);
      setSyncMessage("Unable to start stream report sync right now.");
    } finally {
      setSyncingReports(false);
    }
  };

  const handleRowClick = (row: TopStreamItem) => {
    navigate(`/stream-management/view/${row.id}/stream-report`, {
      state: {
        streamTitle: row.name,
        serviceType: "FuneralHome",
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 2, lg: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {syncMessage && (
            <Box
              sx={{
                color: "text.secondary",
                fontSize: 13,
                backgroundColor: "#F3F6F9",
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
              }}
            >
              {syncMessage}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSyncReports}
                disabled={syncingReports}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 500,
                }}
              >
                {syncingReports ? "Syncing..." : "Sync stream reports"}
              </Button>
            </motion.div>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <StreamLocationsCard
                  totalViews={totalViews}
                  data={streamLocationsData}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <ViewsCard
                  platforms={viewsData.platforms}
                  values={viewsData.values}
                  colors={viewsData.colors}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <TopStreamsTable
                rows={topStreamsData}
                onRowClick={handleRowClick}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <CountryViewsMapCard data={countryViewsData} />

            <Box sx={{ mt: 3 }}>
              <PlatformBreakdownCard
                platforms={viewsData.platforms}
                values={viewsData.values}
                colors={viewsData.colors}
              />
            </Box>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};

export default StreamsDashboard;
